import React, { ChangeEvent, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { inventoryService, InventoryType } from '../../services/inventory.service';
import { showErrorMessage, showSuccessMessage } from '../../helper/showMessage';

interface EditInventoryModalProps {
    visible: InventoryType | null;
    setVisible: (inventory: InventoryType | null) => void;
}

type FormType = {
    trailerId: string;
}

export default function EditInventoryModal({ visible, setVisible }: EditInventoryModalProps): React.ReactElement {
    const [error, setError] = useState<{ [key: string]: string }>({});
    const [form, setForm] = useState<FormType>({
        trailerId: ""
    });
    const [isUpdating, setIsUpdating] = useState(false);

    // Update form when modal opens with inventory data
    useEffect(() => {
        if (visible) {
            setForm({
                trailerId: visible.trailerId
            });
            setError({});
        }
    }, [visible]);

    async function ErrorHandler(data: FormType) {
        let err: { [key: string]: string } = {};
        
        if (!data.trailerId || data.trailerId.trim() === '') {
            err.trailerId = 'Trailer ID cannot be empty';
        }
        
        setError(err);
        return Object.keys(err).length > 0;
    }

    async function SubmitHandler(event: React.FormEvent) {
        event.preventDefault();
        
        if (!visible) return;

        const isValidated = await ErrorHandler(form);
        if (isValidated) return;

        try {
            setIsUpdating(true);
            await inventoryService.update({ 
                id: visible.id, 
                trailerId: form.trailerId.trim() 
            });
            
            showSuccessMessage('Inventory updated successfully');
            setVisible(null);
        } catch (error: any) {
            showErrorMessage(error?.response?.data?.error || error.message || 'Failed to update inventory');
        } finally {
            setIsUpdating(false);
        }
    }

    if (!visible) return <></>;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            {isUpdating && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/20">
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-lg">
                        <Icon icon="eos-icons:loading" className="text-xl animate-spin text-green" />
                        <span className="font-[Geist-Medium] text-sm">Updating...</span>
                    </div>
                </div>
            )}
            
            <form onSubmit={SubmitHandler}>
                <div className="w-[90vw] md:w-[75vw] lg:w-[65vw] xl:w-[55vw] max-h-[95vh] max-content bg-white rounded-lg shadow-lg p-6 flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <h2 className="font-[Geist-Bold] text-lg md:text-xl text-green">
                            Edit Inventory
                        </h2>
                        <button
                            type="button"
                            onClick={() => setVisible(null)}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <Icon icon="gridicons:cross" className="text-xl" />
                        </button>
                    </div>

                    {/* Form Content */}
                    <div className="flex-1 overflow-y-auto px-3 space-y-4">
                        {/* URL Preview Section */}
                        <div className="space-y-2">
                            <label className="block font-[Geist-Medium] text-sm text-gray-700">
                                Image Preview
                            </label>
                            <div className="border border-gray-3 rounded-lg p-4 bg-gray-50">
                                {visible.url ? (
                                    <div className="space-y-3">
                                        <img
                                            src={visible.url}
                                            alt="Trailer Image"
                                            className="w-full max-w-4xl mx-auto rounded-lg shadow-sm"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                const errorDiv = target.nextElementSibling as HTMLElement;
                                                if (errorDiv) errorDiv.classList.remove('hidden');
                                            }}
                                        />
                                        <div className="hidden text-center text-gray-500">
                                            <Icon icon="material-symbols:image-not-supported" className="text-4xl mx-auto mb-2" />
                                            <p className="font-[Geist-Medium] text-sm">Image failed to load</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="font-[Geist-Medium] text-xs text-gray-600 break-all">
                                                {visible.url}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-gray-500 py-8">
                                        <Icon icon="material-symbols:image-not-supported" className="text-4xl mx-auto mb-2" />
                                        <p className="font-[Geist-Medium] text-sm">No image URL available</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Trailer ID Input */}
                        <div className="space-y-2">
                            <label className="block font-[Geist-Medium] text-sm text-gray-700">
                                Trailer ID *
                            </label>
                            <input
                                type="text"
                                value={form.trailerId}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => 
                                    setForm(prev => ({ ...prev, trailerId: event.target.value }))
                                }
                                className="w-full h-10 px-4 border border-gray-3 rounded-lg focus:outline-none focus:border-green font-[Geist-Medium] transition-colors"
                                placeholder="Enter trailer ID"
                            />
                            {error.trailerId && (
                                <p className="text-red-500 text-xs font-[Geist-Medium]">{error.trailerId}</p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center items-center gap-6 pt-5 border-t border-gray-3">
                        <button
                            type="button"
                            onClick={() => setVisible(null)}
                            className="w-40 h-12 bg-gray-3 hover:bg-gray-2 text-white rounded-lg transition-colors font-[Geist-Medium] text-sm border border-gray-3"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="w-40 h-12 bg-green hover:bg-green-600 disabled:opacity-50 text-white rounded-lg transition-colors font-[Geist-Medium] text-sm border border-green"
                        >
                            {isUpdating ? 'Updating...' : 'Update'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
